import { FC, useRef, useState, useEffect } from "react";
import { Box } from "@mui/material";
import { iDetailListInputProps } from "../../../types/contentType";
import { DEFAULT_DATE_FILTER_FORMAT, fDate } from "../../../utils/formatTime";
import palette from "../../../theme/palette";
import DoneIcon from "../../../assets/svg-fc/DoneIcon";
import CrossIconSvg from "../../../assets/svg-fc/CrossIconSvg";
import {
	EMAIL_REGEX,
	PHONE_REGEX,
	NUMBERS_ONLY_WITH_PLUS_SIGN_REGEX,
	REMOVE_NON_DIGITS_FROM_STRING,
	REMOVE_SPACE_FROM_STRING,
} from "../../../utils/regex";
import ErrorText from "../error-text/ErrorText";
import "./detailListInput.scss";

const DetailListInput: FC<iDetailListInputProps> = ({
	icon,
	value: initialValue,
	optional,
	inputType,
	min,
	max,
	minLength,
	maxLength,
	setIsEditing,
	onUpdate,
	errorMessage,
	...props
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const ignoreBlurRef = useRef(false);
	const formattedInitialValue =
		initialValue === "None"
			? ""
			: inputType === "date"
				? fDate(initialValue, DEFAULT_DATE_FILTER_FORMAT)
				: initialValue;
	const [value, setValue] = useState<string>(formattedInitialValue);
	const showErrorMessage =
		(min && value < min) ||
		(max && value > max) ||
		((!optional || value) && minLength && value.length < minLength) ||
		(inputType === "email" && !value.match(EMAIL_REGEX)) ||
		(inputType === "tel" && !value.match(PHONE_REGEX));
	const isDisabled = (!optional && !value) || showErrorMessage;

	const openDatePicker = () => {
		inputRef.current?.showPicker();
	};

	const onUpdateHandler = () => {
		if (!isDisabled) {
			setIsEditing(false);
			onUpdate(value);
		}
	};

	useEffect(() => {
		setValue(formattedInitialValue);
	}, [formattedInitialValue]);

	return (
		<>
			<Box
				className="editTextHold"
				onMouseDown={() => {
					ignoreBlurRef.current = true;
				}}
				onMouseUp={() => {
					ignoreBlurRef.current = false;
				}}
				onClick={(e) => {
					e.stopPropagation();
					ignoreBlurRef.current = false;
				}}
			>
				<Box className="editTextHoldRow" sx={{ cursor: isDisabled ? "default" : "pointer" }}>
					{icon && (
						<span
							className="iconHold"
							onClick={() => {
								inputType === "date" && openDatePicker();
							}}
						>
							{icon}
						</span>
					)}
					<input
						ref={inputRef}
						type={inputType}
						autoFocus
						value={value}
						min={min}
						max={max}
						minLength={minLength}
						maxLength={maxLength}
						style={{
							color: inputType === "date" && !value ? palette.grey[600] : palette.secondary.main,
						}}
						onClick={(e) => {
							e.stopPropagation();
							inputType === "date" && openDatePicker();
						}}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								onUpdateHandler();
							}
						}}
						onChange={(e) => {
							const newValue =
								inputType === "tel"
									? e.target.value.replace(REMOVE_SPACE_FROM_STRING, "")
									: inputType === "number"
										? e.target.value.replace(REMOVE_NON_DIGITS_FROM_STRING, "")
										: e.target.value;
							if (inputType === "number" && newValue !== "" && newValue.length > (maxLength as number)) {
								return;
							}
							if (inputType === "tel" && !NUMBERS_ONLY_WITH_PLUS_SIGN_REGEX.test(newValue)) {
								return;
							}
							setValue(newValue);
						}}
						onBlur={() => {
							if (ignoreBlurRef.current) {
								ignoreBlurRef.current = false;
								return;
							}
							setIsEditing(false);
						}}
						{...props}
					/>
				</Box>
				<Box className="editTextHoldRow">
					<Box
						className="iconContainer"
						onClick={(e) => {
							e.stopPropagation();
							onUpdateHandler();
						}}
						sx={{
							bgcolor: isDisabled ? palette.grey[600] : palette.primary.main,
							cursor: isDisabled ? "default" : "pointer"
						}}>
						<DoneIcon />
					</Box>
					<Box
						className="iconContainer"
						onClick={(e) => {
							e.stopPropagation();
							setIsEditing(false);
						}}
						sx={{
							bgcolor: "#F2F2F2",
							color: "#9CA4AB",
							cursor: "pointer"
						}}>
						<CrossIconSvg size="10" crosIconHandler={() => setIsEditing(false)} />
					</Box>
				</Box>
			</Box>
			{showErrorMessage && <ErrorText errorMessage={errorMessage} />}
		</>
	);
};

export default DetailListInput;
